import React, { useEffect, useState } from 'react';

import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { SelectButton } from 'primereact/selectbutton';

import NewLoginForm from "@/react_app/NewAuth/NewLoginForm/NewLoginForm";
import NewLoginSocials from "@/react_app/NewAuth/NewLoginSocials/NewLoginSocials";
import NewRestorePasswordDialog from "@/react_app/NewAuth/NewRestorePasswordDialog/NewRestorePasswordDialog";

import { CheckedIcon, NotCheckedIcon } from '@/react_app/img/Icons';

import { UserType } from '@/data/types';
import { emailRegex } from "@/utils/globals/regex";
import { useAuth, useToastModule, useUsers } from "@/modules";
import { minPasswordLength, minUsernameLength, MESSAGE_WRAPPER, TYPE_OPTIONS } from "@/utils/globals/constants";

import "./NewRegistrationFull.css";

export default function (props) {

  const users = useUsers();
  const { loadUser } = useAuth();
  const toast = useToastModule();
  const [userType, setUserType] = useState('');
  const [registrationStep, setRegistrationStep] = useState(1);
  const [verificationCode, setVerificationCode] = useState('');
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [verificationCodeError, setVerificationCodeError] = useState(false);
  const [userDetails, setUserDetails] = useState({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    register: ''
  });

  const {
    username, firstName, lastName, email, password
  } = userDetails;

  useEffect(() => {
    if (/@/gi.test(username)) {
      setErrors(prevErrors => ({ ...prevErrors, username: MESSAGE_WRAPPER['invalidUsername'] }));
      localStorage.removeItem('username');
    } else {
      setErrors(prevErrors => ({ ...prevErrors, username: prevErrors.username }));
      localStorage.setItem('username', username);
    }
  }, [userDetails])

  const renderOptionItem = (option) => (
    <div className={'optionItem'}>
      {userType === option.value ? <CheckedIcon /> : <NotCheckedIcon />}
      {option.label}
    </div>
  );

  const sendVerificationCode = async () => {
    setVerificationCodeError(false);
    const response = await users.manager.verificationEmail(email);
    if (response) toast.showSuccess("Verification code sent successfully");
    return response
  }

  const handleUserType = () => {
    if (userType === 'influencer') {
      setUserType(UserType.Creator);
    } else {
      setUserType(UserType.BrandOwner);
    }
  }

  const handleChange = (key, value) => {
    setUserDetails((prev) => ({ ...prev, [key]: value }));
    if (key !== 'register') setErrors((prev) => ({ ...prev, [key]: (value) ? "" : MESSAGE_WRAPPER[key] }))
  }

  const registrationViaEmail = async () => {
    const newErrors = { ...errors };

    if (!username || username.length < minUsernameLength) {
      newErrors.username = MESSAGE_WRAPPER['username'];
    }

    if (!firstName) newErrors.firstName = MESSAGE_WRAPPER['firstName'];
    if (!lastName) newErrors.lastName = MESSAGE_WRAPPER['lastName'];

    if (!email) {
      newErrors.email = MESSAGE_WRAPPER['email'];
    } else if (!emailRegex.test(email)) {
      newErrors.email = MESSAGE_WRAPPER['invalidEmail'];
    }

    if (password.length < minPasswordLength) {
      newErrors.password = MESSAGE_WRAPPER['password'];
    }

    setErrors(newErrors);

    if (!Object.values(newErrors).some(error => error !== '')) {
      try {
        const response = await sendVerificationCode();
        if (response) setRegistrationStep(3);
      } catch (error) {
        setErrors(prevErrors => ({ ...prevErrors, register: MESSAGE_WRAPPER['verificationCode'] }));
      }
    }
  };

  const registerWithCode = async () => {
    const tokens = await users.manager.signUp(
      email,
      firstName,
      lastName,
      password,
      userType,
      verificationCode,
      username,
    );
    if (!tokens) {
      setVerificationCodeError(true);
      return;
    }
    loadUser(tokens?.tokens.access.token)
      .then((success) => {
        if (success) {
          props?.router.push({ name: 'creator-account-setup', params: { creatorID: success?.id ? success?.id : "" } });
          localStorage.setItem('new-design-toggle', 'activated');
        }
      });
  };

  useEffect(() => {
    const type = localStorage.getItem('login-view-Type');
    setUserType((type) ? (type === 'brandOwner') ? 'brand' : 'influencer' : '');
    return () => {
      localStorage.removeItem('login-view-Type')
    }
  }, []);

  return (
    <div className={'registration-full'}>
      <p className={'registerText'}>Register</p>
      {registrationStep === 1 &&
        <div>
          <p className={'bottomText'}>Select your preferred profile type for system registration.</p>
          <SelectButton id={"selectButton"} value={userType} onChange={(e) => {
            setUserType(e.value)
            localStorage.setItem('login-view-Type', e.value == "brand" ? 'brandOwner' : 'creator');
          }} options={TYPE_OPTIONS} optionLabel="label" itemTemplate={renderOptionItem} />
          {userType === TYPE_OPTIONS[0].value &&
            <div className={'registrationType'}>
              <div className={'registrationTypeTitle'}>
                <svg xmlns="http://www.w3.org/2000/svg" width="26" height="23" viewBox="0 0 26 23" fill="none">
                  {/* eslint-disable-next-line max-len,vue/max-len */}
                  <path d="M26 0V4.92033H24.375V1.64011H1.625V16.4011H13V18.0412H0V0H26ZM19.5 4.92033C20.3971 4.92033 21.2393 5.09118 22.0264 5.43287C22.8135 5.77456 23.5033 6.24011 24.0957 6.82953C24.6882 7.41894 25.1536 8.11514 25.4922 8.91811C25.8307 9.72108 26 10.5753 26 11.4808C26 12.3863 25.8392 13.2191 25.5176 13.9794C25.196 14.7396 24.7262 15.4487 24.1084 16.1064L23.7021 16.5421C23.6006 16.6531 23.4948 16.777 23.3848 16.9136C23.2747 17.0503 23.1774 17.2041 23.0928 17.3749C23.0081 17.5458 22.9362 17.7081 22.877 17.8618C22.8177 18.0156 22.7881 18.1779 22.7881 18.3487V20.5014C22.7881 20.8516 22.7246 21.1762 22.5977 21.4752C22.4707 21.7742 22.293 22.039 22.0645 22.2696C21.8359 22.5003 21.5736 22.6754 21.2773 22.795C20.9811 22.9146 20.6595 22.9829 20.3125 23H18.6875C18.3405 23 18.0189 22.9359 17.7227 22.8078C17.4264 22.6797 17.1641 22.5003 16.9355 22.2696C16.707 22.039 16.5335 21.7785 16.415 21.488C16.2965 21.1976 16.2288 20.8687 16.2119 20.5014V18.3487C16.2035 18.1352 16.1569 17.9259 16.0723 17.7209C15.9876 17.5159 15.8818 17.3194 15.7549 17.1315C15.6279 16.9435 15.4883 16.7642 15.3359 16.5933C15.1836 16.4225 15.0355 16.2602 14.8916 16.1064C14.2822 15.4572 13.8167 14.7525 13.4951 13.9922C13.1735 13.2319 13.0085 12.3948 13 11.4808C13 10.5753 13.1693 9.72535 13.5078 8.93092C13.8464 8.13649 14.3076 7.4403 14.8916 6.84234C15.4756 6.24438 16.1654 5.77456 16.9609 5.43287C17.7565 5.09118 18.6029 4.92033 19.5 4.92033ZM21.0869 19.7198H17.9131V20.5014C17.9131 20.7149 17.9893 20.8986 18.1416 21.0524C18.2939 21.2061 18.4759 21.283 18.6875 21.283H20.3125C20.5241 21.283 20.7061 21.2061 20.8584 21.0524C21.0107 20.8986 21.0869 20.7149 21.0869 20.5014V19.7198ZM21.2012 18.0028C21.2858 17.5159 21.4212 17.0973 21.6074 16.7471C21.7936 16.3968 22.0052 16.0722 22.2422 15.7733C22.4792 15.4743 22.7204 15.1924 22.9658 14.9276C23.2113 14.6628 23.4398 14.3681 23.6514 14.0435C23.863 13.7188 24.0365 13.3558 24.1719 12.9543C24.3073 12.5528 24.375 12.0617 24.375 11.4808C24.375 10.7974 24.248 10.1567 23.9941 9.55877C23.7402 8.96082 23.3932 8.43974 22.9531 7.99554C22.513 7.55135 21.9967 7.20111 21.4043 6.94485C20.8118 6.68858 20.1771 6.56045 19.5 6.56045C18.8229 6.56045 18.1882 6.68858 17.5957 6.94485C17.0033 7.20111 16.487 7.55135 16.0469 7.99554C15.6068 8.43974 15.2598 8.96082 15.0059 9.55877C14.752 10.1567 14.625 10.7974 14.625 11.4808C14.625 12.13 14.7477 12.7578 14.9932 13.3643C15.2386 13.9708 15.5898 14.5175 16.0469 15.0045C16.2754 15.2351 16.4827 15.4572 16.6689 15.6708C16.8551 15.8843 17.0244 16.1107 17.1768 16.3499C17.3291 16.589 17.4561 16.8368 17.5576 17.093C17.6592 17.3493 17.7396 17.6526 17.7988 18.0028H21.2012Z" fill="#ffffff" />
                </svg>
                <span className={'registrationTypeBigText'}>Influencer Registration</span>
              </div>
              <p className={'registrationTypeSmallText'}>Registering as an influencer gives you access to campaigns published by gaming companies, developers and publishers.</p>
              <p className={'registrationTypeSmallText'}>Earn money by applying to campaigns and delivering meaningful content.</p>
            </div>
          }
          {userType === TYPE_OPTIONS[1].value &&
            <div className={'registrationType'}>
              <div className={'registrationTypeTitle'}>
                <svg xmlns="http://www.w3.org/2000/svg" width="26" height="23" viewBox="0 0 26 23" fill="none">
                  {/* eslint-disable-next-line max-len,vue/max-len */}
                  <path d="M26 0V4.92033H24.375V1.64011H1.625V16.4011H13V18.0412H0V0H26ZM19.5 4.92033C20.3971 4.92033 21.2393 5.09118 22.0264 5.43287C22.8135 5.77456 23.5033 6.24011 24.0957 6.82953C24.6882 7.41894 25.1536 8.11514 25.4922 8.91811C25.8307 9.72108 26 10.5753 26 11.4808C26 12.3863 25.8392 13.2191 25.5176 13.9794C25.196 14.7396 24.7262 15.4487 24.1084 16.1064L23.7021 16.5421C23.6006 16.6531 23.4948 16.777 23.3848 16.9136C23.2747 17.0503 23.1774 17.2041 23.0928 17.3749C23.0081 17.5458 22.9362 17.7081 22.877 17.8618C22.8177 18.0156 22.7881 18.1779 22.7881 18.3487V20.5014C22.7881 20.8516 22.7246 21.1762 22.5977 21.4752C22.4707 21.7742 22.293 22.039 22.0645 22.2696C21.8359 22.5003 21.5736 22.6754 21.2773 22.795C20.9811 22.9146 20.6595 22.9829 20.3125 23H18.6875C18.3405 23 18.0189 22.9359 17.7227 22.8078C17.4264 22.6797 17.1641 22.5003 16.9355 22.2696C16.707 22.039 16.5335 21.7785 16.415 21.488C16.2965 21.1976 16.2288 20.8687 16.2119 20.5014V18.3487C16.2035 18.1352 16.1569 17.9259 16.0723 17.7209C15.9876 17.5159 15.8818 17.3194 15.7549 17.1315C15.6279 16.9435 15.4883 16.7642 15.3359 16.5933C15.1836 16.4225 15.0355 16.2602 14.8916 16.1064C14.2822 15.4572 13.8167 14.7525 13.4951 13.9922C13.1735 13.2319 13.0085 12.3948 13 11.4808C13 10.5753 13.1693 9.72535 13.5078 8.93092C13.8464 8.13649 14.3076 7.4403 14.8916 6.84234C15.4756 6.24438 16.1654 5.77456 16.9609 5.43287C17.7565 5.09118 18.6029 4.92033 19.5 4.92033ZM21.0869 19.7198H17.9131V20.5014C17.9131 20.7149 17.9893 20.8986 18.1416 21.0524C18.2939 21.2061 18.4759 21.283 18.6875 21.283H20.3125C20.5241 21.283 20.7061 21.2061 20.8584 21.0524C21.0107 20.8986 21.0869 20.7149 21.0869 20.5014V19.7198ZM21.2012 18.0028C21.2858 17.5159 21.4212 17.0973 21.6074 16.7471C21.7936 16.3968 22.0052 16.0722 22.2422 15.7733C22.4792 15.4743 22.7204 15.1924 22.9658 14.9276C23.2113 14.6628 23.4398 14.3681 23.6514 14.0435C23.863 13.7188 24.0365 13.3558 24.1719 12.9543C24.3073 12.5528 24.375 12.0617 24.375 11.4808C24.375 10.7974 24.248 10.1567 23.9941 9.55877C23.7402 8.96082 23.3932 8.43974 22.9531 7.99554C22.513 7.55135 21.9967 7.20111 21.4043 6.94485C20.8118 6.68858 20.1771 6.56045 19.5 6.56045C18.8229 6.56045 18.1882 6.68858 17.5957 6.94485C17.0033 7.20111 16.487 7.55135 16.0469 7.99554C15.6068 8.43974 15.2598 8.96082 15.0059 9.55877C14.752 10.1567 14.625 10.7974 14.625 11.4808C14.625 12.13 14.7477 12.7578 14.9932 13.3643C15.2386 13.9708 15.5898 14.5175 16.0469 15.0045C16.2754 15.2351 16.4827 15.4572 16.6689 15.6708C16.8551 15.8843 17.0244 16.1107 17.1768 16.3499C17.3291 16.589 17.4561 16.8368 17.5576 17.093C17.6592 17.3493 17.7396 17.6526 17.7988 18.0028H21.2012Z" fill="#ffffff" />
                </svg>
                <span className={'registrationTypeBigText'}>Brand Registration</span>
              </div>
              <p className={'registrationTypeSmallText'}>Registering as a brand gives you access to campaigns creation and collaborations with influencers to promote your games.</p>
              <p className={'registrationTypeSmallText'}>Push your game to the next level by running scalable campaigns, reaching new audiences and increasing your ROI.</p>
            </div>
          }
          <Button className={'registerButton'} disabled={!userType} onClick={() => { setRegistrationStep(2); handleUserType(); }}>Register</Button>
          <div className={'centerText'}>
            <p className={'existingRegistrationText'}>Already have a registration?</p>
            <p className={'redUnderlineText'} onClick={() => props.handleRouting('auth/login')}>Sign in</p>
          </div>
        </div>
      }
      {registrationStep === 2 &&
        <div>
          <p className={'formText p-mt-5'}>Sign up with your social media</p>
          <NewLoginSocials action={'register'} onConnect={props.handleUserConnect} userType={userType} connecting={props.connecting} username={username} />
          <p className={'formText'}>Or sign up with email</p>
          <div className={'mb-16'}>
            <p className={'inputFieldLabel'}>{userType === UserType.Creator ? userType : "brand"} name <span className="required">*</span></p>
            <InputText className={'inputNameField'}
              onChange={(e) => handleChange('username', e.target.value)}
            />
            <div className={'registerError'} >{errors.username && <p>{errors.username}</p>}</div>
          </div>
          <div className="first-last-wrap">
            <div className="input-wrap">
              <p className={'inputFieldLabel'}>First Name <span className="required">*</span></p>
              <InputText className={'inputField'}
                onChange={(e) => handleChange('firstName', e.target.value)} />
              <div className={'registerError'} >{errors.firstName && <p>{errors.firstName}</p>}</div>
            </div>
            <div className="input-wrap">
              <p className={'inputFieldLabel'}>Last Name <span className="required">*</span></p>
              <InputText className={'inputField'} onChange={(e) => handleChange('lastName', e.target.value)} />
              <div className={'registerError'} >{errors.lastName && <p>{errors.lastName}</p>}</div>
            </div>
          </div>
          <NewLoginForm errors={errors} handleChange={handleChange} />
          <div className={'registerError'} >{errors.register && <p>{errors.register}</p>}</div>
          <Button className={'registerButton'} onClick={registrationViaEmail}>Register</Button>
          <div className={'centerText'}>
            <p className={'existingRegistrationText'}>Already have a registration??</p>
            <p className={'redUnderlineText'} onClick={() => props.handleRouting('auth/login')}>Sign in</p>
          </div>
        </div>
      }
      {registrationStep === 3 &&
        <div>
          <p className={'bottomText'}>We have sent a code to your e-mail {email}. To verify it, please enter it here:</p>
          <InputText className={'inputField'} placeholder={"Enter the verification code"} onChange={(e) => setVerificationCode(e.target.value)} />
          {verificationCodeError && <p className={'codeError'}>The verification code is invalid.</p>}
          <p className={'sendCodeAgainText'}>Didn't receive the code? <span className={'redUnderlineTextSmall'} onClick={sendVerificationCode}>Send again.</span></p>
          <Button className={'registerButton'} onClick={registerWithCode}>REGISTER</Button>
          <div className={'centerText'}>
            <p className={'existingRegistrationText'}>Already have a registration?</p>
            <p className={'redUnderlineText'} onClick={() => props.handleRouting('auth/login')}>Sign in</p>
          </div>
        </div>
      }
      <NewRestorePasswordDialog />
    </div>
  )
}
